import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
} from '@angular/core';
import { mapDescriptionAttrsToPanelFields } from '@epicuro-next/dynamic-elements/utilities';
import { selectAttributesPriorityConfig } from '@epicuro-next/platform/configuration';
import { Articles } from '@epicuro-next/state/replenishment-state';
import { isNonNullish } from '@epicuro-next/utilities';
import { select, Store } from '@ngrx/store';
import { Product, ProductMedia } from 'graphql-api/gql-api-interfaces';
import { BehaviorSubject, combineLatest, filter } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  selector: 'atx-product-card-actions',
  styleUrls: ['./product-card-actions.component.scss'],
  templateUrl: './product-card-actions.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ProductCardActionsComponent {
  private store = inject(Store);
  public product$ = new BehaviorSubject<Product | null>(null);
  public replenishments$ = new BehaviorSubject<Articles | null>(null);

  @Input() public addedToActiveList: boolean;
  @Input() public currentVariantMedia: ProductMedia[] = [];
  @Input() public deletable = false;
  @Input() public showBookmarkIcon = true;
  @Input() public isBookmarking = false;
  @Input() public previewAttributes = false;
  @Input() set product(product: Product) {
    this.product$.next(product);
  }
  @Input() set replenishments(articles: Articles | null) {
    this.replenishments$.next(articles);
  }
  @Output() public readonly addProductToActiveList = new EventEmitter<void>();
  @Output() public readonly deleteFromProductList = new EventEmitter<void>();
  @Output() public readonly removeProductFromActiveList =
    new EventEmitter<void>();

  private readonly attributesPriorityConfig$ = this.store.pipe(
    select(selectAttributesPriorityConfig),
  );

  public readonly panelDef$ = combineLatest([
    this.product$.pipe(filter(isNonNullish)),
    this.attributesPriorityConfig$,
  ]).pipe(
    map(([product, attributesConfig]) =>
      mapDescriptionAttrsToPanelFields(product, attributesConfig),
    ),
  );

  public readonly hasReplenishment$ = combineLatest([
    this.product$,
    this.replenishments$,
  ]).pipe(
    map(([product, replenishments]) => {
      if (!product || !replenishments) {
        return false;
      }

      const { parentId, productId } = product;

      const replenishmentData = replenishments[parentId ?? productId];
      if (!replenishmentData) {
        return false;
      }

      return replenishmentData.materials?.some((material) =>
        material.sizes?.some((size) => size.quantity),
      );
    }),
  );

  protected onAddProductToActiveList() {
    this.addProductToActiveList.emit();
  }

  protected onRemoveProductFromActiveList() {
    this.removeProductFromActiveList.emit();
  }

  public onDeleteFromProductList() {
    this.deleteFromProductList.emit();
  }
}
